/**
 * Talk-aware phone links (`c-phone-link.vue`). When Talk is in play for the user, clicking one opens
 * a small menu for that number — text it, call it, or search calls/messages for it — instead of
 * handing straight off to the device's dialer.
 *
 * Left entirely alone (native `tel:` behaviour) when the user has no Talk access and no in-app
 * dialer, when the number can't be read, or when the only action would be a device call. Call is
 * always on the menu: through Talk where it can dial, else the device's `tel:` handler.
 *
 * On a phone browser the Talk web screens are deliberately not offered, so the menu is Call only
 * (ZynoTalk's dial link). Inside the ZynoSuite mobile app every action is offered: Talk routes hand
 * off to the matching ZynoTalk screen there (see zynotalk-handoff.ts).
 */

import { showContextMenu } from '@zyno-io/vue-foundation';

import { GlobalState } from '@/global';
import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { DataCache } from '@/shared/data/cache';
import { hasRouteAccess } from '@/shared/helpers/permissions.helpers';
import { canDialInApp, canDialViaTalk, requestDial, whenDialerReady } from '@/shared/services/dialer.service';
import { filters } from '@/vf.setup';

import { canSendSmsNumber, canViewSmsNumber } from '../helpers/messaging';
import { showZynoTalkLauncher } from '../zynotalk-launcher';
import { useTalkHelpers } from './store';

type ContextMenuItems = Parameters<typeof showContextMenu>[1]['items'];

// Built once and reused: `useTalkHelpers` creates a computed of its own each call. Its backing
// DataCache refs are stable across tenant switches (they reload in place), so one instance is safe.
let talkHelpers: ReturnType<typeof useTalkHelpers> | undefined;

function hasSmsNumber(canUse: typeof canViewSmsNumber): boolean {
    const { hasNumberPermission } = (talkHelpers ??= useTalkHelpers());
    const numbers = DataCache.getRef('talkNumbers');
    return !!numbers.value?.some(n => !n.deletedAt && canUse(n) && hasNumberPermission(n, 'sms'));
}

/** Whether the user has any number they could send a text from. */
export function canSendText(): boolean {
    return hasSmsNumber(canSendSmsNumber);
}

/** Whether the user can see any messaging number's conversations (sending not required). */
function canViewTexts(): boolean {
    return hasSmsNumber(canViewSmsNumber);
}

/** Call history filtered to these remote numbers (E.164 — history is matched on stored numbers). */
export function searchCalls(label: string, phoneNumbers: string[]) {
    void GlobalState.router.push({ name: 'talk-cdr', query: { phones: phoneNumbers.join(','), phonesLabel: label } });
}

/**
 * The dialable part of a `tel:` href: URI parameters (`;ext=`, `;phone-context=`), post-dial digits
 * (`,`/`p`/`w` pauses, an `x`/`ext` extension) and visual separators dropped, keeping a leading `+`,
 * digits and `*`/`#`. Null when nothing dialable remains.
 */
export function telHrefNumber(href: string): string | null {
    let value = href.replace(/^tel:/i, '');
    try {
        value = decodeURIComponent(value);
    } catch {
        // A malformed escape: read the raw text rather than give up on the link.
    }
    value = value.split(/[;,]|p|w|x|ext/i)[0]!;
    const dialable = value.trim().replace(/[^+\d*#]/g, '');
    const number = dialable.startsWith('+') ? `+${dialable.replace(/\+/g, '')}` : dialable.replace(/\+/g, '');
    return /\d/.test(number) ? number : null;
}

/**
 * The number as E.164 when it can be read as one: `+`-prefixed passes through, NANP digits (10, or
 * 11 with a leading 1) gain `+1`. Anything else (an extension, a short code) is null — texting and
 * history search only work on stored E.164 numbers.
 */
export function telNumberE164(number: string): string | null {
    if (/^\+\d{8,15}$/.test(number)) return number;
    const match = number.match(/^1?(\d{10})$/);
    return match ? `+1${match[1]}` : null;
}

/**
 * The number from a `?dial=` URL parameter, normalised like a `tel:` href (E.164 when readable).
 * An unencoded `+` in a query string decodes to a space, so a leading space before digits is read
 * back as the `+` it was.
 */
export function dialParamNumber(raw: string): string | null {
    const number = telHrefNumber(`tel:${raw.replace(/^ (?=\d)/, '+')}`);
    return number && (telNumberE164(number) ?? number);
}

/**
 * Dial a number handed in by URL (`/talk?dial=`): the in-app dialer on desktop, ZynoTalk on a phone,
 * else the device's `tel:` handler — the same paths as `requestDial`, once the dialer has had a
 * chance to register.
 */
export async function dialFromUrl(raw: string): Promise<void> {
    const number = dialParamNumber(raw);
    if (!number) return;
    await whenDialerReady();
    requestDial(number);
}

/** The menu for a clicked `tel:` number — empty when nothing applies, so the caller can step aside. */
export function buildTelLinkActionItems(number: string): ContextMenuItems {
    const e164 = telNumberE164(number);
    const items: ContextMenuItems = [];
    // Whether the Talk routes the non-Call actions open are usable here (see the header comment).
    const talkScreens = !showZynoTalkLauncher() || MobileBridge.isWithinZynoSuiteMobile;

    if (e164 && talkScreens && hasRouteAccess('/talk/sms') && canSendText()) {
        items.push({ title: 'Text', handler: () => void GlobalState.router.push({ name: 'talk-sms', query: { to: e164 } }) });
    }

    // Through Talk where it can dial (in-app dialer, ZynoTalk), else the device's own `tel:` handler.
    items.push({ title: 'Call', handler: () => requestDial(e164 ?? number) });

    if (e164 && talkScreens && hasRouteAccess('/talk/cdr') && (talkHelpers ??= useTalkHelpers()).hasCallHistoryAccess.value) {
        const label = filters.phone(e164) ?? e164;
        items.push({ title: 'Search for calls', handler: () => searchCalls(label, [e164]) });
    }

    if (e164 && talkScreens && hasRouteAccess('/talk/sms') && canViewTexts()) {
        items.push({ title: 'Search for messages', handler: () => void GlobalState.router.push({ name: 'talk-sms', query: { q: e164 } }) });
    }

    return items;
}

/**
 * A phone link's click: opens the Talk menu for `phoneNumber` in place of the link's `tel:` hand-off.
 * Leaves the click to the link (native `tel:`) when it's modified (new window, copy link, …), when
 * there's no Talk and no in-app dialer, when the number can't be read, or when the only action would
 * be a device call.
 */
export function handleTelLinkClick(event: MouseEvent, phoneNumber: string) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // Talk gate first, before touching any Talk data: no Talk, no in-app dialer → native tel:.
    if (!canDialInApp() && !hasRouteAccess('/talk')) return;
    const number = telHrefNumber(`tel:${phoneNumber}`);
    if (!number) return;
    const items = buildTelLinkActionItems(number);
    // Only a device call on offer: that's what the link does already.
    if (items.length === 1 && !canDialViaTalk()) return;

    // The menu replaces the link's activation, so a surrounding row's click handler shouldn't fire
    // underneath it either.
    event.preventDefault();
    event.stopPropagation();
    // From Enter (a click with no pointer position) the menu opens at the link, keyboard-driven.
    showContextMenu(event, { items });
}
